import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../../config";
import BcValueCircle from "./BcValueCircle";
import FilterableTable from "./FilterableTable";

// "Inventory Over 90" widget — green circle showing the total cost value of
// remaining inventory aged over 90 days (excluding the Commercial cost center),
// read live from Business Central. Firm-wide metric (not salesperson-scoped).
//
// Clicking the circle opens a modal with the per-item breakdown
// (GET /Dashboard/api/Dashboard/InventoryOver90Detail), filterable per column.
// This detail has no invoice due date; it carries the item, its quantities, its
// cost, and the posting date instead.
const COLUMNS = [
  { key: "itemNo", label: "Item No" },
  { key: "jobNumber", label: "Job Number" },
  { key: "entryType", label: "Entry Type" },
  { key: "unitOfMeasure", label: "Unit Of Measure" },
  { key: "description", label: "Job Description" },
  { key: "quantity", label: "Quantity", type: "number" },
  { key: "remainingQuantity", label: "Remaining Quantity", type: "number" },
  { key: "costAmountActual", label: "Cost Amount", type: "currency" },
  { key: "postingDate", label: "Posting Date", type: "date", noFilter: true },
];

const InventoryOver90 = ({ auth: { auth } }) => {
  const [value, setValue] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");

  const token = auth && auth[0] ? auth[0].token : null;

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/InventoryOver90`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setValue(rows[0] ? rows[0].value : 0);
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Inventory Over 90:", err.message);
      });
    return () => {
      active = false;
    };
  }, [token]);

  // Fetch the per-item breakdown lazily, the first time the modal opens.
  const openDetails = () => {
    setShowDetails(true);
    if (detailStatus !== "idle" || !token) return;
    setDetailStatus("loading");
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/InventoryOver90Detail`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setDetail(rows);
        setDetailStatus("ok");
      })
      .catch((err) => {
        console.warn("Could not load Inventory Over 90 detail:", err.message);
        setDetailStatus("error");
      });
  };

  return (
    <>
      <BcValueCircle
        title="Inventory Over 90"
        value={value}
        onClick={openDetails}
      />

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="ft-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Inventory Over 90</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailStatus === "loading" ? (
            <div className="ft-status">Loading…</div>
          ) : detailStatus === "error" ? (
            <div className="ft-status">
              Could not load the Inventory Over 90 details.
            </div>
          ) : (
            <FilterableTable
              columns={COLUMNS}
              rows={detail || []}
              emptyText="No inventory is over 90 days old."
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(InventoryOver90);
